import React, { useState } from "react";
import ConfirmationModal from "../../../shared/ConfirmationModal";
import PaginatedTable from "../../../shared/PaginatedTable";
import OtherAttendeeModal from "./OtherAttendeeModal";
import OtherAttendeesTableHeader from "./OtherAttendeesTableHeader";
import OtherAttendeesTableRecord from "./OtherAttendeesTableRecord";

export interface OtherAttendee {
  id?: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  type: string;
  status?: string; // corresponds to TripAttendeeStatuses
  assignedVenueID?: number;
  personId?: number;
}

interface OtherAttendeesProps {
  event: {
    otherAttendees?: OtherAttendee[];
    tripVenues?: Array<{ id: number; name: string }>;
  };
  onChange: (name: string, value: OtherAttendee[]) => void;
}

const RESULTS_PER_PAGE = 12;

export default function OtherAttendees({
  event,
  onChange,
}: OtherAttendeesProps) {
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(RESULTS_PER_PAGE);
  const [keyword, setKeyword] = useState("");
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [attendeeIndex, setAttendeeIndex] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attendeeToDeleteIndex, setAttendeeToDeleteIndex] = useState<
    number | null
  >(null);

  const attendees = event.otherAttendees || [];

  const venueNamesById = new Map(
    (event.tripVenues || []).map((venue) => [venue.id, venue.name]),
  );

  const getVenueDisplay = (attendee: OtherAttendee) => {
    if (attendee.assignedVenueID) {
      return venueNamesById.get(attendee.assignedVenueID) || "";
    }
    return "";
  };

  const loadData = (newPage = 1, newResults = results) => {
    setPage(newPage);
    setResults(newResults);
  };

  const onSearch = (value: string) => {
    setKeyword(value);
    setPage(1); // reset to first page so results aren't hidden on a now-shorter list
  };

  // stable sort by last name, then first name (Array.sort keeps original order on ties)
  const compareByName = (a: OtherAttendee, b: OtherAttendee) => {
    const lastNameComparison = (a.lastName || "").localeCompare(
      b.lastName || "",
      undefined,
      { sensitivity: "base" },
    );
    return lastNameComparison !== 0
      ? lastNameComparison
      : (a.firstName || "").localeCompare(b.firstName || "", undefined, {
          sensitivity: "base",
        });
  };

  const matchesKeyword = (attendee: OtherAttendee) => {
    const term = keyword.toLowerCase().trim();
    if (!term) {
      return true;
    }
    return [
      attendee.firstName,
      attendee.lastName,
      attendee.phoneNumber,
      attendee.email,
    ]
      .map((field) => (field ? field.toString().toLowerCase() : ""))
      .join(" ")
      .includes(term);
  };

  const onAddAttendee = () => {
    setAttendeeIndex(null);
    setShowAttendeeModal(true);
  };

  const onEditAttendee = (index: number) => {
    setAttendeeIndex(index);
    setShowAttendeeModal(true);
  };

  const onDeleteAttendee = (index: number) => {
    setAttendeeToDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const saveAttendee = (attendee: OtherAttendee) => {
    const newAttendees = [...attendees];
    if (attendeeIndex === null) {
      newAttendees.push(attendee);
    } else {
      newAttendees[attendeeIndex] = attendee;
    }
    onChange("otherAttendees", newAttendees);

    setAttendeeIndex(null);
    setShowAttendeeModal(false);
  };

  const deleteAttendee = () => {
    const newAttendees = [...attendees];
    if (attendeeToDeleteIndex !== null) {
      newAttendees.splice(attendeeToDeleteIndex, 1);
    }
    onChange("otherAttendees", newAttendees);

    setAttendeeToDeleteIndex(null);
    setShowDeleteModal(false);
  };

  // tag index before filter/sort so edit/delete still map to the correct full-list element
  const filteredAttendees = attendees
    .map((attendee, index) => ({ ...attendee, attendeeIndex: index }))
    .filter(matchesKeyword)
    .sort(compareByName);

  const paginationStart = (page - 1) * results;
  const pagedAttendees = filteredAttendees.slice(
    paginationStart,
    paginationStart + results,
  );

  return (
    <React.Fragment>
      <div className="program-settings-page-section">
        <div className="flex flex-align-center flex-justify-space">
          <p className="program-settings-page-title xxl-text fw-500 mb-0">
            Other attendees
          </p>
          <button
            className="btn custom-btn btn-accent uppercase-text btn-medium"
            onClick={onAddAttendee}
          >
            Add
          </button>
        </div>
        <p className="accent-text mt-8">
          You can view, filter, and export these attendees on the Attendees
          &amp; Check-in tab
        </p>
      </div>
      <div className="program-settings-page-section">
        <div className="other-attendee-table">
          <PaginatedTable
            loading={false}
            loadData={loadData}
            page={page}
            filterComponent={
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search"
                  value={keyword}
                  onChange={(e) => onSearch(e.target.value)}
                />
                <i className="material-icons accent-text-secondary flex flex-align-center">
                  search
                </i>
              </div>
            }
            records={pagedAttendees}
            renderHeader={() => <OtherAttendeesTableHeader />}
            renderRow={(
              attendee: OtherAttendee & { attendeeIndex: number },
            ) => (
              <OtherAttendeesTableRecord
                key={attendee.attendeeIndex}
                attendee={attendee}
                venueName={getVenueDisplay(attendee)}
                onEditAttendee={() => onEditAttendee(attendee.attendeeIndex)}
                onRemoveAttendee={() =>
                  onDeleteAttendee(attendee.attendeeIndex)
                }
              />
            )}
            results={results}
            showResultsCount={true}
            success={true}
            totalCount={filteredAttendees.length}
          />
        </div>
      </div>

      {showAttendeeModal && (
        <OtherAttendeeModal
          attendee={attendeeIndex === null ? null : attendees[attendeeIndex]}
          close={() => {
            setAttendeeIndex(null);
            setShowAttendeeModal(false);
          }}
          save={saveAttendee}
          venues={event.tripVenues || []}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          cancel={() => {
            setAttendeeToDeleteIndex(null);
            setShowDeleteModal(false);
          }}
          confirm={deleteAttendee}
          message="Are you sure you want to remove this attendee?"
          show
        />
      )}
    </React.Fragment>
  );
}
